<template>
  <Dashboard title="Custom Content Page" no-padding="true">
    <div v-if="loading" class="flex justify-center items-center h-full w-full">
      <Loader />
    </div>
    <div v-else-if="routine === null" class="w-full m-4">
      <div class="bg-gray-900 p-4 text-text-secondary text-xs tracking-[1.5px]">
        {{ $t('training.routine.not_found') }}
        <router-link class="underline" to="/routines">{{ $t('training.routine.go_back') }}</router-link>
      </div>
    </div>
    <div v-else class="w-full h-full flex flex-col relative">
      <div class="flex flex-col">
        <div class="flex relative flex-col">
          <div class="flex w-full px-9 py-12 z-20">
            <div
              class="flex flex-col lg:flex-row gap-y-3 w-full border-b pb-12 border-gray-800"
            >
              <div class="flex flex-col gap-3 flex-1">
                <div class="flex flex-row gap-4">
                  <h1 class="text-text-primary font-semibold text-4xl">
                    {{ routine?.name }}
                  </h1>
                  <div class="flex gap-2 items-center">
                    <div
                      v-if="routine?.coach_curated"
                      class="text-[10px] rounded-md px-1 py-[1px] self-start"
                      style="
                        background-color: rgba(208, 113, 197, 0.125);
                        color: rgb(208, 113, 197);
                      "
                    >
                      {{ $t('training.routine.coach') }}
                    </div>
                    <button
                      v-if="routine?.coach_curated && routine?.author.id === user.id"
                      @click="refreshCoachRoutine"
                      :disabled="isRefreshing"
                      class="flex items-center justify-center w-6 h-6 rounded-md text-text-primary hover:bg-gray-800 disabled:text-text-secondary duration-300"
                      :title="$t('training.routine.refresh_coach_routine')"
                    >
                      <svg
                        v-if="!isRefreshing"
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      <svg
                        v-else
                        class="w-4 h-4 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          class="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          stroke-width="4"
                        />
                        <path
                          class="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="flex gap-2 items-center">
                  <h2 class="text-text-secondary text-sm font-light">
                    {{ routine?.description }}
                  </h2>
                </div>
              </div>
              <div class="flex flex gap-3">
                <div class="flex flex-col gap-3">
                  <button
                    @click="launchRoutine()"
                    class="bg-primary-600 px-3 hover:bg-primary-500 duration-300 w-full has-tooltip lg:w-auto text-text-primary rounded-lg flex gap-2 py-2 items-center justify-center"
                  >
                    <img
                      src="../../assets/icons/play.svg"
                      :alt="$t('training.shared.alt_play')"
                      class="w-3 h-3 brightness-200"
                    />
                    <h1 class="text-xs">{{ $t('training.shared.launch_routine') }}</h1>
                  </button>
                  <button
                    @click="cloneRoutine()"
                    class="w-full lg:w-auto text-text-primary bg-gray-800 rounded-lg flex gap-2 py-2 px-3 items-center justify-center"
                  >
                    <h1 class="text-xs">{{ $t('training.routine.clone') }}</h1>
                  </button>
                </div>
                <div class="flex flex-col gap-3">
                  <router-link
                    v-if="routine?.author.id === user.id && !routine?.coach_curated"
                    :to="{ name: 'RoutineEditPage', params: { slug: routine?.slug } }"
                    class="w-full lg:w-auto text-text-primary bg-gray-800 rounded-lg flex gap-2 py-2 px-3 items-center justify-center"
                  >
                    <h1 class="text-xs">{{ $t('common.edit') }}</h1>
                  </router-link>
                  <button
                    v-else-if="routine?.enrolled"
                    class="w-full lg:w-auto text-text-primary bg-gray-800 rounded-lg flex gap-2 py-2 px-3 items-center justify-center"
                  >
                    <CheckIcon class="w-3 h-3 text-text-primary" />
                    <h1 class="text-xs">{{ $t('training.routine.subscribed') }}</h1>
                  </button>
                  <button
                    @click="subscribe"
                    v-else
                    class="w-full lg:w-auto text-text-primary bg-gray-800 rounded-lg flex gap-2 py-2 px-3 items-center justify-center"
                  >
                    <PlusCircleIcon class="w-3 h-3 text-text-primary" />
                    <h1 class="text-xs">{{ $t('common.subscribe') }}</h1>
                  </button>
                  <button
                    class="w-full lg:w-auto text-text-primary bg-gray-800 rounded-lg flex gap-2 py-2 px-3 items-center justify-center"
                    v-clipboard:copy="`http://${url}/routines/${routine?.slug}`"
                    v-clipboard:success="copySucceeded"
                  >
                    <img
                      src="../../assets/icons/copy.svg"
                      :alt="$t('training.shared.alt_copy')"
                      class="w-3 h-3 brightness-200"
                    />
                    <h1 class="text-xs">{{ $t('common.share') }}</h1>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full px-9 z-20">
            <div class="w-full border-b border-gray-800">
              <div class="flex flex-col lg:flex-row gap-12 pb-12 items-start">
                <div class="flex-1 gap-4 flex flex-col">
                  <h2 class="text-text-primary text-lg font-semibold">
                    {{ $t('training.routine.segments_count', { n: tasks.length }) }}
                  </h2>
                  <div class="flex flex-col gap-[2px] w-full">
                    <div
                      v-for="(task, index) in tasks.slice(0, showTasksInt)"
                      class="item p-4 flex items-center gap-1 select-none bg-gray-900"
                      :key="task"
                    >
                      <div
                        class="flex items-center justify-center h-10 w-10 rounded-full bg-gray-800 border border-gray-700 text-sm text-text-primary"
                      >
                        #{{ index + 1 }}
                      </div>
                      <div class="flex flex-col justify-center gap-1 pl-2">
                        <h2 class="text-text-primary">{{ task.name }}</h2>
                        <div class="text-text-secondary flex items-center text-xs gap-2">
                          <h2>{{ task.cs_map.friendly_name }}</h2>
                          <span class="text-text-disabled">•</span>
                          <h2>{{ task.mod }}</h2>
                          <span class="text-text-disabled">•</span>
                          <h2>{{ $t('training.routine.mins', task.estimated_duration_in_minutes || 0) }}</h2>
                          <span v-if="task.presets && task.presets[0]" class="text-text-disabled">•</span>
                          <h2 v-if="task.presets && task.presets[0]">{{ task.presets[0].friendly_name }}</h2>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="showTasksInt < tasks.length"
                      class="item p-2 flex items-center gap-1 select-none bg-gray-900 fadeOut"
                    />
                  </div>
                  <div v-if="showTasksInt < tasks.length">
                    <button
                      @click="showTasksInt += 2"
                      class="w-full text-text-primary bg-gray-800 rounded-lg flex gap-2 py-3 px-3 items-center justify-center"
                    >
                      <h1 class="text-xs">{{ $t('training.routine.show_more') }}</h1>
                    </button>
                  </div>
                  <div v-else-if="showTasksInt > 4">
                    <button
                      @click="showTasksInt = 4"
                      class="w-full text-text-primary bg-gray-800 rounded-lg flex gap-2 py-3 px-3 items-center justify-center"
                    >
                      <h1 class="text-xs">{{ $t('training.routine.show_less') }}</h1>
                    </button>
                  </div>
                </div>
                <div class="grid gap-8 flex-1">
                  <h2 class="text-text-primary text-lg font-semibold">{{ $t('common.details') }}</h2>
                  <div class="flex gap-3">
                    <h3 class="text-text-secondary text-sm font-light flex-1">{{ $t('training.shared.author') }}</h3>
                    <h3 class="text-text-primary text-sm font-semibold">
                      {{ username() }}
                    </h3>
                  </div>
                  <div class="flex gap-3">
                    <h3 class="text-text-secondary text-sm font-light flex-1">{{ $t('common.length') }}</h3>
                    <h3 class="text-text-primary text-sm font-semibold">
                      {{ $t('training.shared.minutes', routine?.total_time_estimation) }}
                    </h3>
                  </div>
                  <div class="flex gap-3">
                    <h3 class="text-text-secondary text-sm font-light flex-1">
                      {{ $t('training.routine.subscriptions') }}
                    </h3>
                    <h3 class="text-text-primary text-sm font-semibold">
                      {{ routine?.students_count }}
                    </h3>
                  </div>
                  <div class="flex gap-3">
                    <h3 class="text-text-secondary text-sm font-light flex-1">
                      {{ $t('training.routine.created_at') }}
                    </h3>
                    <h3 class="text-text-primary text-sm font-semibold">
                      {{ moment(routine?.created_at).format("LLL") }}
                    </h3>
                  </div>
                  <div class="flex gap-3">
                    <h3 class="text-text-secondary text-sm font-light flex-1">
                      {{ $t('training.routine.last_updated') }}
                    </h3>
                    <h3 class="text-text-primary text-sm font-semibold">
                      {{ moment(routine?.updated_at).format("LLL") }}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dashboard>
  <StartServerModal
    :server="serverInfo"
    :show="showServerModal"
    @close="showServerModal = false"
  />
</template>

<script setup>
import axios from 'axios';
import {
  ref, computed, watch, onMounted,
} from 'vue';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import moment from 'moment';
import { CheckIcon, PlayIcon, PlusCircleIcon } from '@heroicons/vue/20/solid';
import {
  Combobox, ComboboxInput, ComboboxOptions, ComboboxOption, ComboboxButton, ComboboxLabel,
} from '@headlessui/vue';
import { useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
import Loader from '../../components/General/Loader.vue';
import Dashboard from '../../components/layouts/DashboardLayout.vue';
import StartServerModal from '../../components/StartServerModal.vue';
import { lengthToTimeMap, timeToLengthMap } from '../../utils/coach_routines';
import { toTitleCase } from '../../utils/formatters';

const { t } = useI18n();
const auth = useAuth();
const router = useRouter();
const route = useRoute();
const user = ref(auth.user());
const store = useStore();
const routine = ref(null);
const loading = ref(true);
const activeServer = computed(() => store.getters.activeServer);
const settings = computed(() => store.getters.siteSettings);
const serverInfo = ref(null);
const showServerModal = ref(false);
const tasks = ref([]);
const selectedSegment = ref(null);
const showTasksInt = ref(4);
const url = ref(location.host);
const presets = ref([]);
const selectedPreset = ref(null);
const isRefreshing = ref(false);

useHead({
  title: () => (routine.value?.name ? t('training.routine.page_title_named', { name: routine.value?.name }) : t('training.routine.page_title')),
  description: t('training.routines.page_description'),
});

function getPresets() {
  axios.get('/presets').then((response) => {
    presets.value = response.data;
  });
}

getPresets();

function cloneRoutine() {
  axios({
    url: `/task_lists/${routine.value.slug}/clone`,
    method: 'POST',
  }).then((res) => {
    store.commit('showMessage', {
      messageType: 'success',
      messageText: t('training.routine.cloned'),
    });
    router.push(`/routines/${res.data.slug}`);
    routine.value = res.data;
  })
    .catch((err) => {
      if (err.response.data.message === 'You already have a clone of this version') {
        store.commit('showMessage', {
          messageType: 'error',
          messageText: t('training.routine.already_cloned'),
        });
      } else {
        store.commit('showMessage', {
          messageType: 'error',
          messageText: t('training.routine.clone_failed'),
        });
      }
    });
}

function getRoutine() {
  loading.value = true;
  let backendUrl = null;
  let params = null;
  if (route.params.slug) {
    // We are on `/routines/:slug`
    backendUrl = `/task_lists/${route.params.slug}`;
    params = { is_exam: false };
  } else {
    // We are on `/coach/:name`
    const duration = lengthToTimeMap[toTitleCase(route.params.name)];
    backendUrl = `/task_lists/coach/${duration}`;
  }
  axios({ method: 'GET', url: backendUrl, params }).then((response) => {
    // NOTE(ssaunier): Auto-navigate `/routines/:slug` to `/coach/:name` if current user is the author's
    if (response.data.coach_curated && response.data.author?.id === user.value.id && route.params.slug) {
      const coachRoutineLength = timeToLengthMap[response.data.duration_in_minutes];
      if (coachRoutineLength) {
        router.replace(`/coach/${coachRoutineLength.toLowerCase()}`).then(() => {
          getRoutine();
        });
        return;
      }
    }

    routine.value = response.data;
    loadTasks();
    selectedSegment.value = tasks.value[0];
    loading.value = false;
  }).catch((err) => {
    if (err.response?.status === 404 && route.params.name) {
      router.push('/coach');
    } else {
      routine.value = null;
      loading.value = false;
    }
  });
}

function copySucceeded() {
  store.commit('showMessage', {
    messageType: 'success',
    messageText: t('training.shared.link_copied'),
  });
  setTimeout(() => {
    store.commit('hideMessage');
  }, 3000);
}

function subscribe() {
  axios({
    url: `/task_lists/${routine.value.slug}/enroll`,
    method: 'POST',
  }).then(() => {
    store.commit('showMessage', {
      messageType: 'success',
      messageText: t('training.routine.subscribed_success'),
    });
    routine.value.enrolled = true;
  });
}

function loadTasks() {
  const allTasks = [];
  routine.value.sections.forEach((section) => {
    section.tasks.forEach((task) => {
      allTasks.push(task);
    });
  });

  tasks.value = allTasks;
}

function username() {
  if (routine.value?.author.username) {
    return routine.value?.author?.username;
  }
  return routine.value?.author?.steam_profile?.personaname;
}

function launchRoutine() {
  const task = routine.value.sections[0].tasks[0];
  serverInfo.value = {
    cs_map: task.cs_map,
    mode: task.mod,
    content: {
      id: parseInt(task.id, 10),
      title: task.name,
      type: 'task',
      task_list_id: routine.value.id,
      task_list_section_id: task.section_id,
      task_list_difficulty: 0,
      is_exam: false,
      use_difficulty: 1,
    },
  };
  showServerModal.value = true;
}

// Refresh coach routine function
async function refreshCoachRoutine() {
  if (isRefreshing.value) return;

  try {
    isRefreshing.value = true;

    const response = await axios.post('/task_lists/refresh_coach_routine');

    if (response.status === 200) {
      // Re-fetch the routine data
      getRoutine();

      // Show success message
      store.commit('showMessage', {
        messageType: 'success',
        messageText: t('training.routine.coach_refreshed'),
      });

      // Hide message after 3 seconds
      setTimeout(() => {
        store.commit('hideMessage');
      }, 3000);
    }
  } catch (error) {
    console.error('Error refreshing coach routine:', error);

    // Show error message
    const errorMessage = error.response?.data?.message || error.response?.data?.error || t('training.routine.coach_refresh_failed');
    store.commit('showMessage', {
      messageType: 'error',
      messageText: errorMessage,
    });

    // Hide message after 5 seconds
    setTimeout(() => {
      store.commit('hideMessage');
    }, 5000);
  } finally {
    isRefreshing.value = false;
  }
}

onMounted(() => {
  if (user.value.access_level === 0) {
    router.push('/upgrade');
  } else {
    getRoutine();
  }
});
</script>

<style scoped>
.fadeOut {
  -webkit-mask-image: -webkit-gradient(
    linear,
    left top,
    left bottom,
    from(rgba(0, 0, 0, 1)),
    to(rgba(0, 0, 0, 0))
  );
}
</style>
